import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { catchError, finalize, tap } from "rxjs/operators";
import { of } from "rxjs";
import { validateFiles } from "../../shared-modules/file-upload-widget/file-upload-widget.util";
import { ServerTokenService } from "../../services/server-token.service";
import { FormFieldOnChangeEvent } from "../../interface";
import { exportAsCsv, parseCsvFile } from "../../utils";
import { COLUMN_CONFIGS, ERRORS, SELECT_CONFIG, UPLOAD_CONFIG } from "./bedboard-settings.constants";
import { BedBoardSettings, BedBoardSettingsColumnConfig, BedBoardSettingsStatus } from "./bedboard-settings.interface";
import { FileUtils } from "../../utils/FIleUtils";

@Component({
  selector: 'bedboard-settings',
  templateUrl: './bedboard-settings.component.html',
  styleUrls: ['./bedboard-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BedBoardSettingsComponent implements OnInit {

  bedboardSettings: BedBoardSettings[] = [];

  columnConfigs: BedBoardSettingsColumnConfig[] = [ ...COLUMN_CONFIGS ];

  unassignedByEhr: Record<string, number> = {};

  generalError = '';
  
  dataLoading = false;

  readonly UPLOAD_CONFIG = UPLOAD_CONFIG;
  readonly SELECT_CONFIG = SELECT_CONFIG;

  constructor(
    private server: ServerTokenService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.initData();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files[0]) {
      return;
    }
    const error = validateFiles(input.files, UPLOAD_CONFIG);
    if (error) {
      this.generalError = error;
      return;
    }
    this.uploadCsvData(input.files[0]);
  }

  onDownloadCsv(): void {
    const csvData = exportAsCsv(this.bedboardSettings, COLUMN_CONFIGS.map(({ key }) => key).concat('bed_status'));
    const dateString = new Date().toISOString().slice(0, 10);
    const blob = new Blob(['\uFEFF', csvData], { type: 'text/csv;charset=utf-8;' });
    FileUtils.donloadLocalFile(blob, `bedboard-settings_${dateString}.csv`);
  }

  onSelectChange({ value }: FormFieldOnChangeEvent, _id: string): void {
    this.updateField({ 
      _id: _id, 
      bed_status: value as BedBoardSettingsStatus,
    });
  }

  private initData(): void {
    this.getData();
  }

  private getData(): void {
    this.dataLoading = true;
    this.server.getWithToken<BedBoardSettings[]>('snfs/admin/getBedBoardSettings').pipe(
      tap(facilitySettings => {
        this.bedboardSettings = facilitySettings;
      }),
      catchError(error => {
        console.error(error);
        this.generalError = ERRORS.GET;
        return of(null);
      }),
      finalize(() => {
        this.dataLoading = false;
        this.cdr.markForCheck();
      })
    ).subscribe();
  }

  private async uploadCsvData(csvFile: File): Promise<void> {
    const csvData = await parseCsvFile(csvFile);
    this.server.postWithToken<BedBoardSettings[]>('snfs/admin/importBedboardSettingsFromSql', csvData).pipe(
      tap(facilitySettings => {
        this.bedboardSettings = facilitySettings;
      }),
      catchError(error => {
        console.error(error);
        this.generalError = ERRORS.IMPORT;
        return of(null);
      }),
      finalize(() => {
        this.cdr.markForCheck();
      })
    ).subscribe();
  }

  private async updateField(
    rowToUpdate: Partial<BedBoardSettings>,
  ): Promise<void> {
    this.server.postWithToken<BedBoardSettings>('snfs/admin/updateBedboardSetting', { rowToUpdate }).pipe(
      tap(updatedRow => {
        this.bedboardSettings = this.bedboardSettings.map(row => {
          if (row._id === updatedRow._id) {
            return { ...row, ...updatedRow };
          }
          return row;
        });
      }),
      catchError(error => {
        console.error(error);
        this.generalError = ERRORS.UPDATE;
        return of(null);
      }),
      finalize(() => {
        this.cdr.markForCheck();
      })
    ).subscribe();
  }

  trackById (_: number, row: BedBoardSettings) {
    return row._id
  }

  trackByKey (_: number, column: BedBoardSettingsColumnConfig) {
    return column.label;
  }

  get tableLoading(): boolean {
    return this.dataLoading;
  }
}